import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { SelectionButton } from '~/bootcamp/Activities/Fractions/components/SelectionButton'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { getRangesByDifficulty } from './constants'
import { generateTerms } from './helpers'

function FractionCompareNumEqual({
  answerState,
  difficultyLevel,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('bootcamp')
  const [answer, setAnswer] = useState<number | null>(null)
  const [rightSideSelected, setRightSideSelected] = useState<boolean | null>(null)
  const [isSmallest, setIsSmallest] = useState<boolean>(true)

  const [numerator1, setNumerator1] = useState<number>(1)
  const [numerator2, setNumerator2] = useState<number>(2)

  const [denominator, setDenominator] = useState<number>(1)

  const [taskHistory, setTaskHistory] = useState<number[]>([])

  const { resetHistory } = getRangesByDifficulty(difficultyLevel)

  useEffect(() => {
    setAnswer(null)
    setIsSmallest(Math.random() < 0.5)
    setRightSideSelected(null)
    const termValue = generateTerms(difficultyLevel, taskHistory)
    setNumerator1(termValue.numerator1)
    setNumerator2(termValue.numerator2)
    setDenominator(termValue.denominator)
  }, [difficultyLevel, taskHistory])

  // denominator is 1 only on the placeholder first render.
  useEmitInstance(
    onInstanceChange,
    denominator > 1
      ? `\\frac{${numerator1}}{${denominator}} and \\frac{${numerator2}}{${denominator}}; ` +
          `pick the ${isSmallest ? 'smallest' : 'largest'}`
      : null,
  )

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    let selectedAns = null
    if (isSmallest) {
      selectedAns = Math.min(numerator1 / denominator, numerator2 / denominator)
      onAnswerStateChange(selectedAns === answer)
    } else {
      selectedAns = Math.max(numerator1 / denominator, numerator2 / denominator)
      onAnswerStateChange(selectedAns === answer)
    }
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [denominator, ...prevState].slice(0, resetHistory)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
  }

  const handleOnOptionClick = (boolValue: boolean): void => {
    setRightSideSelected(boolValue)
    if (boolValue) {
      setAnswer(numerator2 / denominator)
    } else {
      setAnswer(numerator1 / denominator)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="flex w-full justify-center  items-center  text-sm font-normal text-black pb-12 ">
          {t('fractions.comparing_text', {
            largestSmallest: t(isSmallest ? 'fractions.smallest' : 'fractions.largest'),
          })}
        </div>
        <div className="flex space-x-8">
          <SelectionButton
            isSelected={rightSideSelected === false}
            isWrong={rightSideSelected === false && answerState === false}
            onClick={() => {
              handleOnOptionClick(false)
            }}
            size="medium"
          >
            <div className="w-6  text-xl flex flex-col items-center mx-2">
              {numerator1}
              <Divider className={`h-0.5 ${rightSideSelected === false ? 'bg-white' : ''}`} />
              {denominator}
            </div>
          </SelectionButton>
          <SelectionButton
            isSelected={rightSideSelected === true}
            isWrong={rightSideSelected === true && answerState === false}
            onClick={() => {
              handleOnOptionClick(true)
            }}
            size="medium"
          >
            <div className="w-6  text-xl flex flex-col items-center mx-2">
              {numerator2}
              <Divider className={`h-0.5 ${rightSideSelected === true ? 'bg-white' : ''}`} />
              {denominator}
            </div>
          </SelectionButton>
        </div>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== null,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => {
            setAnswer(null)
            setRightSideSelected(null)
          },
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default FractionCompareNumEqual
